/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {TagsTabKey} from './TagsCoachingText';

export interface TagsEmptyStateProps {
  activeTab: TagsTabKey;
}

// per Figma's "Tags Accordion Assigned/Nearby Empty State" text layers ...
const EMPTY_STATE_TEXT: Record<TagsTabKey, string> = {
  assigned : 'No assigned tags at this time. To assign tags, go to Admin → Assets.',
  nearby   : 'No tags nearby at this time.'
};

const useStyles = makeStyles(() => ({
  container : {
    display        : 'flex',
    width          : '100%',
    alignItems     : 'center',
    justifyContent : 'center',
    padding        : '24px 0'
  },
  text : {
    maxWidth      : '244px',
    fontFamily    : 'Inter',
    fontWeight    : 600,
    fontSize      : '13px',
    lineHeight    : '14px',
    letterSpacing : '0.5px',
    textAlign     : 'center',
    color         : dlsColor('grey2')
  }
}));

/**
 * TagsEmptyState renders the centered empty-state message shown when the
 * active Tags accordion tab (ION-7463) has no rows - "No assigned tags..."
 * for the Assigned tab, "No tags nearby..." for the Nearby/In Range tab.
 *
 * ```typescript
 *
 *   import {TagsEmptyState} from
 *     '@ion/dls/src/components/assetdetails/current/TagsEmptyState';
 *   ...
 *
 *   <TagsEmptyState activeTab='assigned' />
 *   <TagsEmptyState activeTab='nearby' />
 *
 * ```
 *
 */
export const TagsEmptyState = ({activeTab}: TagsEmptyStateProps) => {
  const classes = useStyles();
  return (
    <div className={classes.container}>
      <span className={classes.text}>
        {EMPTY_STATE_TEXT[activeTab]}
      </span>
    </div>
  );
};

export default memo(TagsEmptyState);
